"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatEther } from "viem";
import { berachain } from "viem/chains";
import { useAccount } from "wagmi";
import { REWARD_VAULT_AQUABERA_ADDRESS } from "@/constants/contracts";
import { useBlockRefetch } from "@/hooks/use-block-refetch";
import { extractAPR, extractTVL, fetchRewardVaultData } from "@/lib/berachain-api";
import {
  useReadRewardVaultBalanceOf,
  useReadRewardVaultEarned,
  useWriteRewardVaultGetReward,
} from "@/lib/onchain/generated";
import { formatTokenIntelligent } from "@/utils";
import { useWriteWithToast } from "../use-write-with-toast";

type Hash = `0x${string}`;

interface UseAquaberaVaultDataOptions {
  // Optional: run when the claim transaction confirms
  onClaimSuccess?: (txHash?: Hash) => void;
}

export function useAquaberaVaultData(options?: UseAquaberaVaultDataOptions) {
  const { address } = useAccount();

  // State for API data
  const [apr, setApr] = useState<number>(0);
  const [tvl, setTvl] = useState<number>(0);
  const [depositors, setDepositors] = useState<number>(0);
  const [isLoadingApi, setIsLoadingApi] = useState(true);

  // Fetch vault data from Berachain API
  useEffect(() => {
    const fetchVaultData = async () => {
      setIsLoadingApi(true);
      try {
        const data = await fetchRewardVaultData(REWARD_VAULT_AQUABERA_ADDRESS);

        if (data) {
          const aprValue = extractAPR(data);
          const tvlValue = extractTVL(data);

          setApr(aprValue);
          setTvl(tvlValue);
          // Note: depositors count is not available in the API, keeping mock value
          setDepositors(342); // This could be fetched from a different source if available
        } else {
          // Show 0 for rate limit or no data
          setApr(0);
          setTvl(0);
          setDepositors(0);
        }
      } catch (error: any) {
        // Check if it's a rate limit error
        if (error?.message?.includes("rate limit") || error?.response?.status === 429) {
        }
        // Show 0 for errors/rate limits
        setApr(0);
        setTvl(0);
        setDepositors(0);
      } finally {
        setIsLoadingApi(false);
      }
    };

    fetchVaultData();
    // Refresh every 60 seconds
    const interval = setInterval(fetchVaultData, 60000);
    return () => clearInterval(interval);
  }, []);

  // Read staked LP balance in the reward vault
  const {
    data: stakedBalance,
    isLoading: isLoadingBalance,
    refetch: refetchBalance,
  } = useReadRewardVaultBalanceOf({
    address: REWARD_VAULT_AQUABERA_ADDRESS as `0x${string}`,
    args: address ? [address] : undefined,
    chainId: berachain.id,
    query: {
      enabled: !!address,
      refetchInterval: 10000, // Refresh every 10 seconds
    },
  });

  // Read earned BGT rewards
  const {
    data: earnedRewards,
    isLoading: isLoadingRewards,
    refetch: refetchRewards,
  } = useReadRewardVaultEarned({
    address: REWARD_VAULT_AQUABERA_ADDRESS as `0x${string}`,
    args: address ? [address] : undefined,
    chainId: berachain.id,
    query: {
      enabled: !!address,
      refetchInterval: 10000, // Refresh every 10 seconds
    },
  });

  // Claim BGT rewards
  const { writeContractAsync: claimRewards } = useWriteRewardVaultGetReward();

  // Format staked balance for display
  const formattedStakedBalance = useMemo(() => {
    if (!stakedBalance) return "0";
    return formatTokenIntelligent(stakedBalance, 18, true);
  }, [stakedBalance]);

  // Format earned rewards for display
  const formattedEarnedRewards = useMemo(() => {
    if (!earnedRewards) return "0";
    return formatTokenIntelligent(earnedRewards, 18, true);
  }, [earnedRewards]);

  // Check if there are rewards to claim
  const hasRewards = useMemo(() => {
    return earnedRewards ? earnedRewards > 0n : false;
  }, [earnedRewards]);

  // Keep track of the last sent tx hash so onSuccess can reference it
  const lastSentHashRef = useRef<Hash | undefined>(undefined);

  const claimTx = useWriteWithToast({
    title: "Claim BGT rewards",
    onSuccess: async () => {
      await refetchRewards();
      // Invoke consumer callback after receipt success
      options?.onClaimSuccess?.(lastSentHashRef.current);
    },
  });

  // Keep vault balances fresh every 3 blocks to reduce RPC load.
  useBlockRefetch({
    chainId: berachain.id,
    every: 3,
    enabled: !!address,
    refetch: [refetchBalance, refetchRewards],
  });

  // Function to claim rewards from Aquabera vault
  const handleClaimRewards = useCallback(async () => {
    if (!address || !hasRewards) {
      return;
    }

    try {
      const sent = await claimTx.run(() =>
        claimRewards({
          address: REWARD_VAULT_AQUABERA_ADDRESS as `0x${string}`,
          args: [address, address],
        }),
      );
      if (sent) lastSentHashRef.current = sent;
    } catch {}
  }, [address, hasRewards, claimRewards, claimTx.run]);

  return {
    // Balances
    stakedBalance,
    formattedStakedBalance,
    earnedRewards,
    formattedEarnedRewards,

    // Stats
    apr,
    tvl,
    depositors,
    hasRewards,

    // Loading states
    isLoadingBalance,
    isLoadingRewards,
    isLoadingApi,
    isClaiming: claimTx.isLoading,

    // Actions
    handleClaimRewards,
    refetchBalance,
    refetchRewards,
  };
}
